import React, { useState } from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import * as FiIcons from 'react-icons/fi';

const { FiSettings, FiSave, FiTrash2, FiInfo, FiDatabase, FiKey, FiBarChart2, FiEye, FiEyeOff, FiServer } = FiIcons;

const Settings = ({ config, onConfigSave, clearAllResults }) => {
  const [localConfig, setLocalConfig] = useState({
    apiKey: config.apiKey || '',
    model: config.model || '',
    endpoint: config.endpoint || 'openai',
    ollamaEndpoint: config.ollamaEndpoint || 'https://llama.pressupwards.com/api/generate',
    useNoThink: config.useNoThink || false
  });
  const [showApiKey, setShowApiKey] = useState(false);
  const [activeSection, setActiveSection] = useState('api');

  const handleSaveConfig = () => {
    // Create a clean config with trimmed values
    const cleanConfig = {
      ...localConfig,
      apiKey: localConfig.apiKey?.trim() || '',
      model: localConfig.model?.trim() || '',
      ollamaEndpoint: localConfig.ollamaEndpoint?.trim() || ''
    };
    
    // For OpenAI and OpenRouter, API key is required
    if ((cleanConfig.endpoint === 'openai' || cleanConfig.endpoint === 'openrouter' || cleanConfig.endpoint === 'custom') && !cleanConfig.apiKey) {
      alert('API Key is required for ' + (cleanConfig.endpoint === 'openai' ? 'OpenAI' : cleanConfig.endpoint === 'openrouter' ? 'OpenRouter' : 'Custom API'));
      return;
    }

    // For Ollama, endpoint URL is required
    if (cleanConfig.endpoint === 'ollama' && !cleanConfig.ollamaEndpoint) {
      alert('Endpoint URL is required for Ollama');
      return;
    }

    if (!cleanConfig.model) {
      alert('Model name is required');
      return;
    }

    onConfigSave(cleanConfig);
    alert('Settings saved successfully');
  };

  const handleClearData = () => {
    if (window.confirm('Are you sure you want to clear all analysis results? This action cannot be undone.')) {
      clearAllResults();
      alert('All analysis results have been cleared');
    }
  };

  // Get suggested models based on selected endpoint
  const getSuggestedModels = () => {
    switch (localConfig.endpoint) {
      case 'openai':
        return ['gpt-3.5-turbo', 'gpt-4o', 'gpt-4o-mini'];
      case 'openrouter':
        return ['anthropic/claude-3-haiku', 'anthropic/claude-3-sonnet', 'google/gemini-pro'];
      case 'ollama':
        return ['llama3', 'llama3:8b', 'mistral', 'mixtral'];
      default:
        return ['gpt-3.5-turbo', 'llama3'];
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="settings-page"
    >
      <div className="page-header">
        <h1 className="page-title">Settings</h1>
      </div>
      <div className="settings-layout">
        <div className="settings-sidebar">
          <div
            className={`settings-nav-item ${activeSection === 'api' ? 'active' : ''}`}
            onClick={() => setActiveSection('api')}
          >
            <SafeIcon icon={FiKey} className="nav-icon" />
            <span>API Configuration</span>
          </div>
          <div
            className={`settings-nav-item ${activeSection === 'data' ? 'active' : ''}`}
            onClick={() => setActiveSection('data')}
          >
            <SafeIcon icon={FiDatabase} className="nav-icon" />
            <span>Data Management</span>
          </div>
          <div
            className={`settings-nav-item ${activeSection === 'about' ? 'active' : ''}`}
            onClick={() => setActiveSection('about')}
          >
            <SafeIcon icon={FiInfo} className="nav-icon" />
            <span>About</span>
          </div>
        </div>
        <div className="settings-content">
          {activeSection === 'api' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>API Configuration</h2>
                <p>Configure the API settings for website analysis</p>
              </div>
              <div className="settings-form">
                <div className="form-group">
                  <label htmlFor="endpoint">API Provider</label>
                  <select
                    id="endpoint"
                    value={localConfig.endpoint}
                    onChange={(e) => setLocalConfig({
                      ...localConfig,
                      endpoint: e.target.value,
                      // Reset model when changing provider to avoid incompatibility
                      model: ''
                    })}
                    className="settings-select"
                  >
                    <option value="openai">OpenAI</option>
                    <option value="openrouter">OpenRouter</option>
                    <option value="ollama">Ollama</option>
                    <option value="custom">Custom API</option>
                  </select>
                  <div className="input-help">
                    Select the AI provider you want to use for analysis
                  </div>
                </div>

                {(localConfig.endpoint === 'openai' || localConfig.endpoint === 'openrouter' || localConfig.endpoint === 'custom') && (
                  <div className="form-group">
                    <label htmlFor="apiKey">API Key</label>
                    <div className="api-key-input">
                      <input
                        id="apiKey"
                        type={showApiKey ? "text" : "password"}
                        value={localConfig.apiKey}
                        onChange={(e) => setLocalConfig({ ...localConfig, apiKey: e.target.value })}
                        placeholder="Enter your API key"
                        className="settings-input"
                      />
                      <button
                        type="button"
                        className="toggle-visibility"
                        onClick={() => setShowApiKey(!showApiKey)}
                      >
                        <SafeIcon icon={showApiKey ? FiEyeOff : FiEye} />
                      </button>
                    </div>
                    <div className="input-help">
                      This key is stored locally and never sent to our servers
                    </div>
                  </div>
                )}

                {localConfig.endpoint === 'ollama' && (
                  <div className="form-group">
                    <label htmlFor="ollamaEndpoint">Ollama Endpoint URL</label>
                    <input
                      id="ollamaEndpoint"
                      type="text"
                      value={localConfig.ollamaEndpoint}
                      onChange={(e) => setLocalConfig({ ...localConfig, ollamaEndpoint: e.target.value })}
                      placeholder="https://llama.pressupwards.com/api/generate"
                      className="settings-input"
                    />
                    <div className="input-help">
                      The URL for the Ollama API endpoint
                    </div>
                  </div>
                )}

                <div className="form-group">
                  <label htmlFor="model">Model</label>
                  <input
                    id="model"
                    type="text"
                    value={localConfig.model}
                    onChange={(e) => setLocalConfig({ ...localConfig, model: e.target.value })}
                    placeholder="Enter model name"
                    className="settings-input"
                  />
                  <div className="model-suggestions">
                    <div className="suggestion-label">Suggested models:</div>
                    <div className="suggestion-chips">
                      {getSuggestedModels().map(model => (
                        <button
                          key={model}
                          className={`suggestion-chip ${localConfig.model === model ? 'active' : ''}`}
                          onClick={() => setLocalConfig({ ...localConfig, model })}
                        >
                          {model}
                        </button>
                      ))}
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <div className="checkbox-container">
                    <label className="checkbox-label">
                      <input
                        type="checkbox"
                        checked={localConfig.useNoThink}
                        onChange={(e) => setLocalConfig({ ...localConfig, useNoThink: e.target.checked })}
                        className="checkbox-input"
                      />
                      <span className="checkbox-text">Add "/nothink" suffix to prompts</span>
                    </label>
                    <div className="input-help">
                      This adds "/nothink" to the end of prompts and removes empty &lt;think&gt;&lt;/think&gt; tags from responses
                    </div>
                  </div>
                </div>

                <div className="form-group">
                  <label htmlFor="fitScoreThreshold">Fit Score Threshold for Opener Generation</label>
                  <input
                    id="fitScoreThreshold"
                    type="number"
                    min="1"
                    max="10"
                    step="1"
                    value={localConfig.fitScoreThreshold || 6}
                    onChange={(e) => setLocalConfig({ ...localConfig, fitScoreThreshold: parseInt(e.target.value) })}
                    className="settings-input"
                  />
                  <div className="input-help">
                    Only generate openers for prospects with a fit score of this value or higher (1-10). Default is 6.
                  </div>
                </div>

                <div className="form-actions">
                  <button className="save-button" onClick={handleSaveConfig}>
                    <SafeIcon icon={FiSave} />
                    <span>Save Configuration</span>
                  </button>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'data' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>Data Management</h2>
                <p>Manage your analysis data and application settings</p>
              </div>
              <div className="data-management">
                <div className="data-card">
                  <div className="data-card-header">
                    <h3>Analysis Results</h3>
                    <p>Clear all website analysis data stored in your browser</p>
                  </div>
                  <div className="data-card-actions">
                    <button className="danger-button" onClick={handleClearData}>
                      <SafeIcon icon={FiTrash2} />
                      <span>Clear All Data</span>
                    </button>
                  </div>
                </div>
                <div className="data-card">
                  <div className="data-card-header">
                    <h3>Local Storage</h3>
                    <p>Information about data stored in your browser</p>
                  </div>
                  <div className="storage-info">
                    <div className="info-item">
                      <span className="info-label">API Configuration</span>
                      <span className="info-value">{config.apiKey || config.ollamaEndpoint ? 'Saved' : 'Not saved'}</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Analysis Results</span>
                      <span className="info-value">Using Local Storage</span>
                    </div>
                    <div className="info-item">
                      <span className="info-label">Data Privacy</span>
                      <span className="info-value">All data stored locally</span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          )}

          {activeSection === 'about' && (
            <div className="settings-section">
              <div className="section-header">
                <h2>About WebAnalyzer</h2>
                <p>Information about this application</p>
              </div>
              <div className="about-content">
                <div className="app-info">
                  <div className="app-logo">
                    <SafeIcon icon={FiBarChart2} className="logo-icon" />
                  </div>
                  <h3>WebAnalyzer</h3>
                  <p className="app-version">Version 1.1.0</p>
                </div>
                <div className="app-description">
                  <p>
                    WebAnalyzer is a tool designed to help identify redesign opportunities and potential for websites. It analyzes websites and provides actionable insights and recommendations.
                  </p>
                  <h4>Features</h4>
                  <ul>
                    <li>Analyze websites for redesign potential</li>
                    <li>Get detailed recommendations and opportunities</li>
                    <li>Score websites based on multiple factors</li>
                    <li>Generate reports andexport data</li>
                    <li>Privacy-focused with local data storage</li>
                    <li>Support for multiple AI providers including OpenAI, OpenRouter, and Ollama</li>
                  </ul>
                  <h4>How It Works</h4>
                  <p>
                    WebAnalyzer uses AI to evaluate websites and identify opportunities for improvement. The analysis is performed using the configured API provider (OpenAI, OpenRouter, Ollama, or custom). All data is stored locally in your browser.
                  </p>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      <style jsx>{`
        .settings-page {padding: 1.5rem;}
        .page-header {margin-bottom: 1.5rem;}
        .page-title {font-size: 1.5rem;font-weight: 600;}
        .settings-layout {display: grid;grid-template-columns: 220px 1fr;gap: 1.5rem;}
        .settings-sidebar {background-color: white;border-radius: 8px;box-shadow: var(--shadow-sm);overflow: hidden;}
        .settings-nav-item {display: flex;align-items: center;gap: 0.75rem;padding: 0.875rem 1rem;cursor: pointer;border-left: 3px solid transparent;}
        .settings-nav-item:hover {background-color: #f9fafb;}
        .settings-nav-item.active {background-color: #f5f3ff;border-left-color: var(--primary-color);}
        .nav-icon {color: var(--text-light);}
        .active .nav-icon {color: var(--primary-color);}
        .settings-content {background-color: white;border-radius: 8px;box-shadow: var(--shadow-sm);padding: 1.5rem;}
        .settings-section {max-width: 800px;}
        .section-header {margin-bottom: 1.5rem;}
        .section-header h2 {font-size: 1.25rem;font-weight: 600;margin: 0 0 0.25rem 0;}
        .section-header p {font-size: 0.875rem;color: var(--text-light);margin: 0;}
        .settings-form {display: flex;flex-direction: column;gap: 1.25rem;}
        .form-group {display: flex;flex-direction: column;gap: 0.5rem;}
        .form-group label {font-size: 0.875rem;font-weight: 500;}
        .settings-input, .settings-select {padding: 0.625rem;border: 1px solid #e5e7eb;border-radius: 8px;font-size: 0.875rem;width: 100%;}
        .settings-input:focus, .settings-select:focus {outline: none;border-color: var(--primary-color);box-shadow: 0 0 0 2px rgba(99,102,241,0.2);}
        .api-key-input {position: relative;display: flex;}
        .toggle-visibility {position: absolute;right: 0.625rem;top: 50%;transform: translateY(-50%);background: none;border: none;color: var(--text-light);cursor: pointer;padding: 0.25rem;}
        .input-help {font-size: 0.75rem;color: var(--text-light);}
        .form-actions {display: flex;justify-content: flex-start;margin-top: 1rem;}
        .save-button {display: flex;align-items: center;gap: 0.5rem;padding: 0.625rem 1.25rem;background-color: var(--primary-color);color: white;border: none;border-radius: 8px;font-size: 0.875rem;font-weight: 500;cursor: pointer;}
        .save-button:hover {background-color: var(--primary-dark);}
        .data-management {display: flex;flex-direction: column;gap: 1.25rem;}
        .data-card {border: 1px solid #e5e7eb;border-radius: 8px;padding: 1.25rem;}
        .data-card-header {margin-bottom: 1.25rem;}
        .data-card-header h3 {font-size: 1rem;font-weight: 600;margin: 0 0 0.25rem 0;}
        .data-card-header p {font-size: 0.875rem;color: var(--text-light);margin: 0;}
        .data-card-actions {display: flex;justify-content: flex-start;}
        .danger-button {display: flex;align-items: center;gap: 0.5rem;padding: 0.625rem 1.25rem;background-color: #fee2e2;color: #b91c1c;border: 1px solid #fecaca;border-radius: 8px;font-size: 0.875rem;font-weight: 500;cursor: pointer;}
        .danger-button:hover {background-color: #fecaca;}
        .storage-info {display: flex;flex-direction: column;gap: 0.75rem;}
        .info-item {display: flex;justify-content: space-between;align-items: center;padding: 0.5rem 0;border-bottom: 1px solid #f3f4f6;}
        .info-item:last-child {border-bottom: none;}
        .info-label {font-size: 0.875rem;color: var(--text-light);}
        .info-value {font-size: 0.875rem;font-weight: 500;}
        .about-content {display: flex;flex-direction: column;gap: 2rem;}
        .app-info {display: flex;flex-direction: column;align-items: center;text-align: center;padding-bottom: 1.5rem;border-bottom: 1px solid #e5e7eb;}
        .app-logo {width: 64px;height: 64px;background-color: var(--primary-light);border-radius: 16px;display: flex;align-items: center;justify-content: center;color: white;margin-bottom: 0.75rem;}
        .logo-icon {font-size: 32px;}
        .app-info h3 {font-size: 1.25rem;font-weight: 600;margin: 0 0 0.25rem 0;}
        .app-version {font-size: 0.875rem;color: var(--text-light);margin: 0;}
        .app-description {font-size: 0.875rem;color: var(--text-light);}
        .app-description h4 {font-size: 1rem;font-weight: 600;margin: 1.25rem 0 0.5rem 0;color: var(--text-color);}
        .app-description ul {padding-left: 1.5rem;margin: 0.5rem 0;}
        .app-description li {margin-bottom: 0.25rem;}
        .model-suggestions {margin-top: 0.5rem;}
        .suggestion-label {font-size: 0.75rem;color: var(--text-light);margin-bottom: 0.5rem;}
        .suggestion-chips {display: flex;flex-wrap: wrap;gap: 0.5rem;margin-bottom: 0.5rem;}
        .suggestion-chip {font-size: 0.75rem;padding: 0.25rem 0.5rem;border-radius: 4px;background-color: #f3f4f6;border: 1px solid #e5e7eb;cursor: pointer;transition: all 0.2s;}
        .suggestion-chip:hover {background-color: #e5e7eb;}
        .suggestion-chip.active {background-color: var(--primary-light);color: white;border-color: var(--primary-color);}
        .checkbox-container {display: flex;flex-direction: column;}
        .checkbox-label {display: flex;align-items: center;gap: 0.5rem;cursor: pointer;margin-bottom: 0.25rem;}
        .checkbox-input {width: 16px;height: 16px;accent-color: var(--primary-color);}
        .checkbox-text {font-size: 0.875rem;}
        @media (max-width: 768px) {
          .settings-layout {grid-template-columns: 1fr;}
          .settings-sidebar {display: flex;overflow-x: auto;}
          .settings-nav-item {flex: 0 0 auto;border-left: none;border-bottom: 3px solid transparent;}
          .settings-nav-item.active {border-left-color: transparent;border-bottom-color: var(--primary-color);}
          .suggestion-chips {flex-wrap: wrap;}
        }
      `}</style>
    </motion.div>
  );
};

export default Settings;